import { Button, CircularProgress, Dialog, IconButton, useMediaQuery } from "@mui/material";
import { LuCircleAlert, LuFilePenLine, LuTriangleAlert, LuX } from "react-icons/lu";
import "./UnsavedChangesDialog.css";

export default function UnsavedChangesDialog({
  open, documentName = "document", isExistingDraft = false,
  canSaveDraft = false, saveState = "idle", saveMode = "draft", onSave, onKeepEditing, onDiscard,
}) {
  const saving = saveState === "saving";
  const failed = saveState === "error";
  const record = saveMode === "record";
  const mobile = useMediaQuery("(max-width: 560px)");
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const title = saving ? (record ? `Saving your ${documentName}...` : "Saving your draft...") : failed ? (record ? "Changes could not be saved" : "Draft could not be saved")
    : canSaveDraft ? "Save your changes?" : "Discard your changes?";
  const description = saving
    ? `Saving your changes. You will leave this page only after your ${record ? documentName : "draft"} is saved successfully.`
    : failed
    ? "Your changes are still here. Check any highlighted fields and your connection, then try again, or continue editing."
    : !canSaveDraft
    ? "Saving a draft is not available for this task. Leaving will permanently discard your unsaved changes."
    : record
    ? `You have unsaved changes to this ${documentName}. Save before leaving, or discard these changes.`
    : isExistingDraft
    ? "You have edited an existing draft. Save your updates before leaving, or discard changes made since your last save."
    : mobile
    ? `Save this ${documentName} as a draft to continue later. It will not be issued.`
    : `You have unsaved changes to this ${documentName}. Save a draft to continue later, or discard these changes.`;
  const StateIcon = failed ? LuCircleAlert : canSaveDraft ? LuFilePenLine : LuTriangleAlert;

  return (
    <Dialog open={open} onClose={saving ? undefined : onKeepEditing}
      disableEscapeKeyDown={saving} aria-labelledby="unsaved-changes-title"
      aria-describedby="unsaved-changes-description" maxWidth={false}
      transitionDuration={reducedMotion ? 0 : undefined}
      className="unsaved-changes-dialog" sx={{ zIndex: 10002 }}
      PaperProps={{ className: "unsaved-changes-dialog__paper", "aria-busy": saving }}>
      <header className="unsaved-changes-dialog__header">
        {saving ? <CircularProgress size={24} thickness={3} aria-label={record ? `Saving ${documentName}` : "Saving draft"} />
          : <StateIcon className={`unsaved-changes-dialog__icon${!canSaveDraft ? " unsaved-changes-dialog__icon--danger" : ""}`} aria-hidden="true" />}
        <h2 id="unsaved-changes-title">{title}</h2>
        <IconButton aria-label="Keep editing and close dialog" disabled={saving} onClick={onKeepEditing}><LuX size={20} /></IconButton>
      </header>
      <div className="unsaved-changes-dialog__body" id="unsaved-changes-description" role={failed ? "alert" : undefined}>
        <p>{description}</p>
      </div>
      <footer className="unsaved-changes-dialog__actions" data-save-state={saveState} data-existing-draft={isExistingDraft}>
        {(canSaveDraft || saving || failed) && (
          <Button className="unsaved-changes-dialog__save" variant="contained" disableElevation disabled={saving} onClick={onSave}>
            {saving ? (record ? "Saving changes..." : "Saving draft...") : failed ? "Try saving again" : record || isExistingDraft ? "Save changes & leave" : "Save draft & leave"}
          </Button>
        )}
        {!saving && <>
          <Button className="unsaved-changes-dialog__keep" autoFocus variant={canSaveDraft || failed ? "outlined" : "contained"} disableElevation onClick={onKeepEditing}>Keep editing</Button>
          <Button className="unsaved-changes-dialog__discard" onClick={onDiscard}>
            {mobile ? "Discard changes & leave" : "Discard & leave"}
          </Button>
        </>}
      </footer>
    </Dialog>
  );
}
